
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MessageSquarePlus, Send, Image as ImageIcon, Loader2, CircleHelp, CreditCard, FileWarning, Package, Clock, X } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { LocalDateTime } from "@/components/ui/local-datetime";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogMedia,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { PACKAGE_LABELS } from "@/lib/wizard-config";

type SenderRole = "USER" | "ADMIN";
type ThreadStatus = "OPEN" | "CLOSED" | "AWAITING_REOPEN";

type ThreadListItem = {
  id: string;
  subject: string;
  status: ThreadStatus;
  createdAt: string;
  updatedAt: string;
  lastMessageAt: string;
  orderId: string | null;
  orderNumber: string | null;
  draftId: string | null;
  packageType: keyof typeof PACKAGE_LABELS | null;
  user: { id: string; email: string; name: string | null };
  latestMessage: { body: string; createdAt: string; senderRole: SenderRole } | null;
  unreadForUser: boolean;
  unreadForAdmin: boolean;
};

type MessageRow = {
  id: string;
  body: string;
  createdAt: string;
  senderRole: SenderRole;
  senderUserId: string | null;
};

type ThreadDetails = {
  id: string;
  subject: string;
  status: ThreadStatus;
  createdAt: string;
  updatedAt: string;
  orderId: string | null;
  orderNumber: string | null;
  draftId: string | null;
  packageType: keyof typeof PACKAGE_LABELS | null;
  user: { id: string; email: string; name: string | null };
  unreadForUser: boolean;
  unreadForAdmin: boolean;
};

type Props = {
  isAdmin: boolean;
  initialThreads?: ThreadListItem[];
  orderOptions?: Array<{ id: string; label: string }>;
  draftOptions?: Array<{ id: string; label: string }>;
  initialCompose?: {
    open?: boolean;
    refKind?: ReferenceKind;
    orderId?: string;
    draftId?: string;
    subject?: string;
    body?: string;
  };
};

export type ReferenceKind = "general" | "order" | "draft";

function shortId(value: string, head = 8): string {
  if (value.length <= head) return value;
  return `${value.slice(0, head)}...`;
}

function RenderMessage({ body, mine }: { body: string; mine: boolean }) {
  const imgRegex = /!\[.*?\]\((https?:\/\/.*?)\)/g;
  const parts = body.split(imgRegex);

  const result: React.ReactNode[] = [];

  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];
    if (i % 2 === 1) {
      result.push(
        <a
          key={`img-${i}`}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className="my-2 block overflow-hidden rounded-lg border border-border/30 bg-black/5 transition-opacity hover:opacity-90"
        >
          <img
            src={part}
            alt="Attachment"
            className="block max-h-96 w-full object-contain"
          />
        </a>,
      );
    } else if (part) {
      const urlRegex = /(https?:\/\/[^\s]+)/g;
      const subParts = part.split(urlRegex);
      for (let j = 0; j < subParts.length; j++) {
        const subPart = subParts[j];
        if (j % 2 === 1) {
          result.push(
            <a
              key={`link-${i}-${j}`}
              href={subPart}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className={cn(
                "underline underline-offset-[3px] break-all transition-opacity hover:opacity-80",
                mine ? "decoration-background/40" : "text-blue-600 decoration-blue-600/30",
              )}
            >
              {subPart}
            </a>,
          );
        } else {
          result.push(subPart);
        }
      }
    }
  }

  return <div className="whitespace-pre-wrap">{result}</div>;
}

export function SupportInbox({
  isAdmin,
  initialThreads = [],
  orderOptions = [],
  draftOptions = [],
  initialCompose,
}: Props) {
  const [threads, setThreads] = useState(initialThreads);
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(initialThreads[0]?.id ?? null);
  const [selected, setSelected] = useState<ThreadDetails | null>(null);
  const [messages, setMessages] = useState<MessageRow[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [composer, setComposer] = useState("");
  const [error, setError] = useState<string | null>(null);

  const [showNewThread, setShowNewThread] = useState(
    !isAdmin && (initialCompose?.open || initialThreads.length === 0),
  );
  const [newSubject, setNewSubject] = useState(initialCompose?.subject ?? "");
  const [newBody, setNewBody] = useState(initialCompose?.body ?? "");
  const [refKind, setRefKind] = useState<ReferenceKind>(
    initialCompose?.refKind ?? "general",
  );
  const [refOrderId, setRefOrderId] = useState(initialCompose?.orderId ?? "");
  const [refDraftId, setRefDraftId] = useState(initialCompose?.draftId ?? "");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [creatingThread, setCreatingThread] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [closing, setClosing] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    if (messages.length > 0) {
      scrollToBottom();
    }
  }, [messages, scrollToBottom]);

  const filteredThreads = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return threads;
    return threads.filter((t) => {
      const packageLabel = t.packageType ? PACKAGE_LABELS[t.packageType].toLowerCase() : "";
      return (
        t.subject.toLowerCase().includes(q) ||
        (t.orderNumber ?? "").toLowerCase().includes(q) ||
        t.user.email.toLowerCase().includes(q) ||
        packageLabel.includes(q)
      );
    });
  }, [query, threads]);

  const loadThreads = useCallback(async () => {
    try {
      const res = await fetch("/api/support/threads", { credentials: "include" });
      const data = (await res.json().catch(() => ({}))) as { threads?: ThreadListItem[] };
      if (!res.ok) return;
      const next = Array.isArray(data.threads) ? data.threads : [];
      const wasEmpty = threads.length === 0;
      setThreads(next);
      if (!selectedId && next[0] && wasEmpty) {
        setSelectedId(next[0].id);
      }
    } catch {
      // keep stale data
    }
  }, [selectedId, threads.length]);

  const loadMessages = useCallback(async (threadId: string) => {
    setLoadingMessages(true);
    setError(null);
    try {
      const res = await fetch(`/api/support/threads/${threadId}/messages`, {
        credentials: "include",
      });
      const data = (await res.json().catch(() => ({}))) as {
        error?: string;
        thread?: ThreadDetails;
        messages?: MessageRow[];
      };
      if (!res.ok) {
        setError(data.error ?? "Could not load conversation.");
        return;
      }
      setSelected(data.thread ?? null);
      setMessages(Array.isArray(data.messages) ? data.messages : []);
      setThreads((prev) =>
        prev.map((t) =>
          t.id === threadId ? { ...t, unreadForAdmin: false, unreadForUser: false } : t,
        ),
      );
    } catch {
      setError("Could not load conversation.");
    } finally {
      setLoadingMessages(false);
    }
  }, []);

  useEffect(() => {
    void loadThreads();
    const poll = window.setInterval(() => void loadThreads(), 20_000);
    return () => window.clearInterval(poll);
  }, [loadThreads]);

  useEffect(() => {
    if (!selectedId) return;
    void loadMessages(selectedId);
    const poll = window.setInterval(() => void loadMessages(selectedId), 8_000);
    return () => window.clearInterval(poll);
  }, [loadMessages, selectedId]);

  async function handleSend() {
    const body = composer.trim();
    if (!selectedId || !body || sending) return;
    setSending(true);
    setError(null);
    try {
      const res = await fetch(`/api/support/threads/${selectedId}/messages`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ body }),
      });
      const data = (await res.json().catch(() => ({}))) as { error?: string; message?: MessageRow };
      if (!res.ok) {
        setError(data.error ?? "Could not send message.");
        return;
      }
      if (data.message) {
        setMessages((prev) => [...prev, data.message as MessageRow]);
      }
      setComposer("");
      void loadThreads();
    } catch {
      setError("Could not send message.");
    } finally {
      setSending(false);
    }
  }

  async function handleImageUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || uploading) return;
    setUploading(true);
    setError(null);
    try {
      const preRes = await fetch("/api/uploads/presign", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ purpose: "support_image", contentType: file.type, sizeBytes: file.size, filename: file.name }),
      });
      const preData = await preRes.json();
      if (!preRes.ok) throw new Error(preData.error || "Upload failed");
      const { uploadUrl, publicUrl } = preData;
      const putRes = await fetch(uploadUrl, { method: "PUT", headers: { "Content-Type": file.type }, body: file });
      if (!putRes.ok) throw new Error("Cloud upload failed");
      const body = `![image](${publicUrl})`;
      if (selectedId) {
        const res = await fetch(`/api/support/threads/${selectedId}/messages`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ body }),
        });
        if (!res.ok) throw new Error("Could not send image message");
        const data = await res.json();
        if (data.message) setMessages((prev) => [...prev, data.message as MessageRow]);
        void loadThreads();
      } else if (showNewThread) {
        const prefix = newBody.trim() ? newBody + "\n\n" : "";
        setNewBody(prefix + body);
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  async function updateThreadStatus(threadId: string, status: ThreadStatus) {
    if (closing) return;
    setClosing(true);
    setError(null);
    try {
      const res = await fetch(`/api/support/threads/${threadId}/status`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "Could not update status");
      }
      setThreads((prev) => prev.map((t) => (t.id === threadId ? { ...t, status } : t)));
      if (selected?.id === threadId) {
        setSelected((prev) => (prev ? { ...prev, status } : null));
        void loadMessages(threadId);
      }
      if (status === "CLOSED") {
        setSelectedId(null);
        setSelected(null);
        setMessages([]);
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setClosing(false);
      setShowCloseModal(false);
    }
  }

  async function createThread() {
    if (creatingThread) return;
    setCreatingThread(true);
    setError(null);
    try {
      const payload: Record<string, unknown> = { subject: newSubject, body: newBody };
      if (refKind === "order" && refOrderId) payload.orderId = refOrderId;
      if (refKind === "draft" && refDraftId) payload.draftId = refDraftId;
      const res = await fetch("/api/support/threads", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = (await res.json().catch(() => ({}))) as { error?: string; thread?: ThreadListItem };
      if (!res.ok) {
        setError(data.error ?? "Could not create conversation.");
        return;
      }
      if (data.thread) {
        setThreads((prev) => [data.thread as ThreadListItem, ...prev]);
        setSelectedId((data.thread as ThreadListItem).id);
      }
      setShowNewThread(false);
      setNewSubject("");
      setNewBody("");
      setRefKind("general");
      setRefOrderId("");
      setRefDraftId("");
    } catch {
      setError("Could not create conversation.");
    } finally {
      setCreatingThread(false);
    }
  }

  return (
    <div className="grid h-full gap-4 lg:grid-cols-[23rem_minmax(0,1fr)]">
      <section className={cn(
        "flex min-h-0 flex-col gap-3 rounded-2xl border border-border bg-card p-3",
        selectedId && "hidden lg:flex"
      )}>
        <div className="flex items-center justify-between gap-2">
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={isAdmin ? "Search by user or subject" : "Search conversations"}
          />
          <Button type="button" variant="outline" size="sm" onClick={() => setShowNewThread((v) => !v)}>
            <MessageSquarePlus className="size-4" />
            New
          </Button>
        </div>

        {showNewThread ? (
          <div className="space-y-2 rounded-xl border border-violet-200 bg-violet-50/50 p-3">
            <Input value={newSubject} onChange={(e) => setNewSubject(e.target.value)} placeholder="Subject" />
            <div className="grid gap-2">
              <Select value={refKind} onValueChange={(v) => setRefKind((v as ReferenceKind) ?? "general")}>
                <SelectTrigger><SelectValue placeholder="What is this about?" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="general"><div className="flex items-center gap-2"><CircleHelp className="size-4 text-muted-foreground" /><span>Something else</span></div></SelectItem>
                  <SelectItem value="order"><div className="flex items-center gap-2"><CreditCard className="size-4 text-muted-foreground" /><span>Paid project</span></div></SelectItem>
                  <SelectItem value="draft"><div className="flex items-center gap-2"><FileWarning className="size-4 text-muted-foreground" /><span>Unpaid draft</span></div></SelectItem>
                </SelectContent>
              </Select>
              {refKind === "order" ? (
                <Select value={refOrderId} onValueChange={(value) => setRefOrderId(value ?? "")}>
                  <SelectTrigger><SelectValue placeholder="Select order" /></SelectTrigger>
                  <SelectContent>
                    {orderOptions.map((o) => (
                      <SelectItem key={o.id} value={o.id}><div className="flex items-center gap-2"><Package className="size-4 text-muted-foreground" /><span className="block max-w-[15rem] truncate">{o.label}</span></div></SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : refKind === "draft" ? (
                <Select value={refDraftId} onValueChange={(value) => setRefDraftId(value ?? "")}>
                  <SelectTrigger><SelectValue placeholder="Select draft" /></SelectTrigger>
                  <SelectContent>
                    {draftOptions.map((d) => (
                      <SelectItem key={d.id} value={d.id}><div className="flex items-center gap-2"><Clock className="size-4 text-muted-foreground" /><span className="block max-w-[15rem] truncate">{d.label}</span></div></SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : null}
            </div>
            <div className="relative rounded-xl border border-border bg-background shadow-[inset_0_1px_0_rgba(255,255,255,0.35)]">
              <Textarea
                className="w-full resize-none border-none bg-transparent px-3.5 pb-10 pt-2.5 shadow-none focus-visible:ring-0 h-[110px]"
                value={newBody}
                onChange={(e) => setNewBody(e.target.value.slice(0, 400))}
                maxLength={400}
                placeholder="Write your message to admin"
              />
              <div className="absolute bottom-2 left-2.5 flex items-center gap-2">
                <input type="file" ref={fileInputRef} onChange={handleImageUpload} accept="image/*" className="hidden" />
                <Button type="button" variant="secondary" size="xs" className="h-7 w-7 rounded-lg border border-border/50 bg-background hover:bg-accent" onClick={() => fileInputRef.current?.click()} disabled={uploading} title="Add image">
                  {uploading ? <Loader2 className="size-3.5 animate-spin text-muted-foreground" /> : <ImageIcon className="size-3.5 text-muted-foreground" />}
                </Button>
                <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground/70">{newBody.length}/400</span>
              </div>
              <div className="absolute bottom-2 right-2">
                <Button type="button" size="sm" variant="default" className="h-7 items-center gap-1.5 px-3 text-[12px]" onClick={() => void createThread()} disabled={creatingThread || !newBody.trim() || !newSubject.trim()}>
                  {creatingThread ? <Loader2 className="size-3 animate-spin" /> : null}
                  {creatingThread ? "Creating..." : "Start chat"}
                </Button>
              </div>
            </div>
          </div>
        ) : null}

        <ul className="no-scrollbar min-h-0 flex-1 space-y-1.5 overflow-y-auto pr-0.5">
          {filteredThreads.map((thread) => {
            const unread = isAdmin ? thread.unreadForAdmin : thread.unreadForUser;
            return (
              <li key={thread.id}>
                <button type="button" onClick={() => setSelectedId(thread.id)} className={cn("w-full rounded-xl border px-3 py-2 text-left transition-colors", selectedId === thread.id ? "border-border bg-accent/60" : "border-border hover:bg-accent/30")}>
                  {isAdmin && (<div className="mb-1 flex items-center justify-between"><span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/80">{thread.user.name || thread.user.email}</span></div>)}
                  <div className="flex items-start justify-between gap-2">
                    <p className="truncate text-sm font-medium text-foreground">{thread.subject}</p>
                    <div className="flex items-center gap-1.5">
                      {thread.status === "CLOSED" ? <Badge tone="neutral" size="sm">Closed</Badge> : thread.status === "AWAITING_REOPEN" ? <Badge tone="warning" size="sm">Awaiting Reopen</Badge> : unread ? <Badge tone="warning" size="sm">Unread</Badge> : null}
                    </div>
                  </div>
                  <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">
                    {thread.latestMessage?.body ? thread.latestMessage.body.startsWith("![image](") ? "📷 Image" : thread.latestMessage.body.replace(/!\[.*?\]\(.*?\)/g, "📷 Image") : "No messages yet."}
                  </p>
                  <div className="mt-1.5 flex items-center gap-2 text-[11px] text-muted-foreground">
                    {thread.orderNumber ? <span>{thread.orderNumber}</span> : null}
                    {thread.packageType ? <span>{PACKAGE_LABELS[thread.packageType]}</span> : null}
                    <span><LocalDateTime iso={thread.lastMessageAt} preset="dateTime" /></span>
                  </div>
                </button>
              </li>
            );
          })}
          {filteredThreads.length === 0 && !(showNewThread && !isAdmin) ? (
            <li className="rounded-xl border border-dashed border-border px-3 py-5 text-center text-sm text-muted-foreground">No conversations found.</li>
          ) : null}
        </ul>
      </section>

      <section className={cn("flex min-h-0 flex-col rounded-2xl border border-border bg-card", !selectedId && "hidden lg:flex")}>
        {selected ? (
          <>
            <header className="flex items-center justify-between border-b border-border px-4 py-3">
              <div className="flex items-center gap-3">
                <Button variant="ghost" size="icon" className="size-8 lg:hidden" onClick={() => setSelectedId(null)}>
                  <X className="size-4" />
                </Button>
                <div>
                  <p className="text-sm font-semibold text-foreground">{selected.subject}</p>
                  <p className="text-xs text-muted-foreground">
                    {selected.orderNumber ? `Reference: ${selected.orderNumber}` : selected.draftId ? `Reference: draft ${shortId(selected.draftId, 12)}` : "General support"}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {isAdmin && selected.status === "OPEN" && (
                  <AlertDialog open={showCloseModal} onOpenChange={setShowCloseModal}>
                    <AlertDialogTrigger render={<Button variant="ghost" size="sm" className="h-8 gap-1.5 px-3 text-muted-foreground hover:bg-red-50 hover:text-red-600" disabled={closing}>{closing ? <Loader2 className="size-3.5 animate-spin" /> : <X className="size-3.5" />}<span className="text-[12px] font-semibold">Close Chat</span></Button>} />
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogMedia><X className="size-6 text-red-600" /></AlertDialogMedia>
                        <AlertDialogTitle>Close this conversation?</AlertDialogTitle>
                        <AlertDialogDescription>This will mark the conversation as closed.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={(e) => { e.preventDefault(); void updateThreadStatus(selected.id, "CLOSED"); }} disabled={closing}>
                          {closing && <Loader2 className="mr-2 size-4 animate-spin" />}Confirm Close
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
                {isAdmin && selected.status === "AWAITING_REOPEN" && (
                  <Button variant="outline" size="sm" className="h-8 gap-1.5 border-emerald-200 bg-emerald-50/50 text-emerald-600 hover:bg-emerald-50" onClick={() => void updateThreadStatus(selected.id, "OPEN")} disabled={closing}>
                    {closing ? <Loader2 className="size-3.5 animate-spin" /> : <CircleHelp className="size-3.5" />}
                    <span className="text-[12px] font-semibold">Accept Reopen</span>
                  </Button>
                )}
                {selected.status === "CLOSED" && (
                  <Button variant="ghost" size="sm" className="h-8 gap-1.5 px-3 text-muted-foreground hover:bg-accent" onClick={() => { setSelectedId(null); setSelected(null); setMessages([]); }}>
                    <X className="size-3.5" /><span className="text-[12px] font-semibold">Deselect</span>
                  </Button>
                )}
              </div>
            </header>

            <div className="no-scrollbar flex-1 space-y-4 overflow-y-auto px-4 py-4">
              {loadingMessages && messages.length === 0 ? (
                <div className="space-y-4">
                  <div className="flex justify-start"><div className="brand-skeleton h-16 w-3/4 rounded-2xl opacity-60" /></div>
                  <div className="flex justify-end"><div className="brand-skeleton h-12 w-1/2 rounded-2xl opacity-40" /></div>
                </div>
              ) : null}
              {messages.map((message) => {
                const mine = isAdmin ? message.senderRole === "ADMIN" : message.senderRole === "USER";
                const isSystemAlert = message.body.startsWith("🔔") || message.body.startsWith("✅") || message.body.startsWith("🔒");
                if (isSystemAlert) {
                  const isReopen = message.body.startsWith("🔔");
                  const isSuccess = message.body.startsWith("✅");
                  const isClosed = message.body.startsWith("🔒");
                  return (
                    <div key={message.id} className="my-4 flex items-center justify-center px-4">
                      <div className={cn("flex items-center gap-2.5 rounded-2xl border px-5 py-2 text-[12.5px] font-semibold shadow-sm", isReopen && "border-amber-200 bg-amber-50/80 text-amber-800", isSuccess && "border-emerald-200 bg-emerald-50/80 text-emerald-800", isClosed && "border-border bg-muted/60 text-muted-foreground")}>
                        <span className="shrink-0 text-base leading-none">{message.body.split(" ")[0]}</span>
                        <span className="leading-tight">{message.body.split(" ").slice(1).join(" ")}</span>
                      </div>
                    </div>
                  );
                }
                return (
                  <div key={message.id} className={cn("flex flex-col gap-1", mine ? "items-end" : "items-start")}>
                    <span className="px-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                      {mine ? "You" : isAdmin ? (selected?.user?.name || selected?.user?.email || "User") : "Admin"}
                    </span>
                    <div className={cn("max-w-[85%] rounded-2xl px-3 py-2 text-sm break-words", mine ? "bg-foreground text-background" : "border border-border bg-muted/40 text-foreground")}>
                      <RenderMessage body={message.body} mine={mine} />
                      <p className={cn("mt-1 text-[11px]", mine ? "text-background/70" : "text-muted-foreground")}>
                        <LocalDateTime iso={message.createdAt} preset="dateTime" />
                      </p>
                    </div>
                  </div>
                );
              })}
              {!loadingMessages && messages.length === 0 ? <p className="text-sm text-muted-foreground">No messages yet.</p> : null}
              {selected.status === "CLOSED" && (
                <div className="my-6 flex flex-col items-center justify-center gap-3 px-4">
                  <div className="flex items-center gap-2.5 rounded-2xl border border-border bg-muted/60 px-5 py-3 text-[13px] font-semibold text-muted-foreground shadow-sm">
                    <X className="size-4" /><span>This conversation was closed</span>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
            <footer className="border-t border-border px-4 py-3">
              {selected.status === "OPEN" ? (
                <>
                  {error ? <p className="mb-2 text-xs text-destructive">{error}</p> : null}
                  <div className="relative rounded-xl border border-border bg-background shadow-[inset_0_1px_0_rgba(255,255,255,0.35)]">
                    <Textarea
                      className="w-full resize-none border-none bg-transparent px-3.5 pb-10 pt-2.5 shadow-none focus-visible:ring-0 h-[110px]"
                      value={composer}
                      onChange={(e) => setComposer(e.target.value.slice(0, 400))}
                      maxLength={400}
                      placeholder={isAdmin ? "Reply to customer..." : "Write a message to admin..."}
                    />
                    <div className="absolute bottom-2 left-2.5 flex items-center gap-2">
                      <input type="file" ref={fileInputRef} onChange={handleImageUpload} accept="image/*" className="hidden" />
                      <Button type="button" variant="secondary" size="xs" className="h-7 w-7 rounded-lg border border-border/50 bg-background hover:bg-accent" onClick={() => fileInputRef.current?.click()} disabled={uploading || sending || !selectedId} title="Add image">
                        {uploading ? <Loader2 className="size-3.5 animate-spin text-muted-foreground" /> : <ImageIcon className="size-3.5 text-muted-foreground" />}
                      </Button>
                      <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground/70">{composer.length}/400</span>
                    </div>
                    <div className="absolute bottom-2 right-2">
                      <Button type="button" size="sm" className="h-7 items-center gap-1.5 px-3 text-[12px]" onClick={() => void handleSend()} disabled={sending || !composer.trim()}>
                        {sending ? <Loader2 className="size-3 animate-spin" /> : <Send className="size-3" />}
                        {sending ? "Sending..." : "Send"}
                      </Button>
                    </div>
                  </div>
                </>
              ) : null}
            </footer>
          </>
        ) : loadingMessages ? (
          <div className="flex animate-in fade-in fill-mode-both flex-col">
            <header className="border-b border-border px-4 py-4">
              <div className="brand-skeleton h-5 w-48 rounded-md" />
            </header>
            <div className="flex-1 space-y-5 px-4 py-6">
              <div className="flex justify-start"><div className="brand-skeleton h-16 w-3/4 rounded-2xl opacity-60" /></div>
            </div>
          </div>
        ) : (
          <div className="m-auto text-center text-sm text-muted-foreground">Select a conversation to start chatting.</div>
        )}
      </section>
    </div>
  );
}
